import styled from '@emotion/styled';
import React, { FC } from 'react';
import { useButton } from '~Hooks';
import useI18n from '~Translations';
import { ContactlessIcon, PersonPlusIcon, SearchIcon } from '../icons';
import {
  Body,
  C,
  ErrorNote,
  ReleasingNote,
  SecondaryAction,
  TRANSITION,
  WarningNote,
  WarningSub,
} from '../styles';

const MethodRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-top: 24px;
`;

const MethodTile = styled.button<{ primary?: boolean; touching?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 116px;
  padding: 0;
  cursor: pointer;
  border-radius: 14px;
  background: ${({ primary, touching }) => {
    if (touching) {
      return primary ? C.accentPressed : C.surfacePressed;
    }
    return primary ? C.accentWash : C.surfaceInset;
  }};
  border: 1px solid
    ${({ primary, touching }) => {
      if (touching) {
        return primary ? C.accent : C.textMuted;
      }
      return primary ? C.accentEdge : C.hairline;
    }};
  color: ${({ primary, touching }) => (primary || touching ? C.textPrimary : C.textSecondary)};
  font-size: 14px;
  font-weight: ${({ primary }) => (primary ? 600 : 500)};
  /* Sinks under the finger so the press is felt on a glass counter screen. */
  transform: ${({ touching }) => (touching ? 'scale(0.97)' : 'scale(1)')};
  transition: border-color ${TRANSITION}, background ${TRANSITION}, transform ${TRANSITION};

  &:hover {
    border-color: ${({ primary }) => (primary ? C.accent : C.textMuted)};
  }

  &:focus-visible {
    outline: 2px solid ${C.accent};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transform: none;
  }
`;

interface Props {
  /** The last card read matched nobody; it is still held for the payment. */
  notFound: boolean;
  /** An unclaimed card is being handed back to the terminal right now. */
  isReleasing: boolean;
  error: string | null;
  /** Whether a card is in hand, which unlocks continuing without a member. */
  hasHeldCard: boolean;
  onSearch: () => void;
  onTapCard: () => void;
  onCreateUser: () => void;
  onKeepCard: () => void;
}

/** The method picker: search, tap a card, or enrol someone new. */
const ChooseStep: FC<Props> = ({
  notFound,
  isReleasing,
  error,
  hasHeldCard,
  onSearch,
  onTapCard,
  onCreateUser,
  onKeepCard,
}) => {
  const { i18n } = useI18n();

  const searchButton = useButton({ onClick: onSearch });
  const tapCardButton = useButton({ onClick: onTapCard });
  const createUserButton = useButton({ onClick: onCreateUser });
  const keepCardButton = useButton({ onClick: onKeepCard });

  return (
    <Body>
      {notFound && (
        <WarningNote>
          <PersonPlusIcon />
          <span>
            {i18n('Loyalty.NotFound')}
            <WarningSub>{i18n('Loyalty.CardStillHeld')}</WarningSub>
          </span>
        </WarningNote>
      )}

      {/* The unclaimed card is being handed back while this is on screen. */}
      {isReleasing && (
        <ReleasingNote>
          <ContactlessIcon size={18} animated />
          {i18n('Loyalty.Cancelling')}
        </ReleasingNote>
      )}

      <MethodRow>
        <MethodTile type="button" {...searchButton}>
          <SearchIcon />
          {i18n('Loyalty.Search')}
        </MethodTile>
        <MethodTile type="button" primary autoFocus {...tapCardButton}>
          <ContactlessIcon />
          {i18n('Loyalty.TapCard')}
        </MethodTile>
      </MethodRow>

      <SecondaryAction type="button" {...createUserButton}>
        {i18n('Loyalty.CreateUser')}
      </SecondaryAction>

      {notFound && hasHeldCard && (
        <SecondaryAction type="button" {...keepCardButton}>
          {i18n('Loyalty.KeepCard')}
        </SecondaryAction>
      )}

      {error && <ErrorNote>{error}</ErrorNote>}
    </Body>
  );
};

export default ChooseStep;
